import React, { useContext, useEffect, useState } from 'react';
import { SCORE_WEIGHTAGE } from '../../utils/Constants';
import LockSwitch from '../../components/LockSwitch';
import { ABContext } from '../../contexts/ABContext';
import { TPayload, TScoreWeightage } from '../../types/ab';

const METRIC_OPTIONS = ['Revenue','Add To Cart Rate','Interactions Conversion Rate','Order Conversion Rate'];

const ScoreWeightage = () => {
  const abContext = useContext(ABContext);
  const [scoreWeightage, setScoreWeightage] = useState<TScoreWeightage[]>([]);

  useEffect(() => {
    if (abContext?.payload?.score_weightage && abContext.payload.score_weightage.length > 0) {
      setScoreWeightage(abContext.payload.score_weightage);
    }
  }, [abContext?.payload?.score_weightage]);

  useEffect(() => {
    if (abContext?.setPayload && scoreWeightage.length > 0) {
      abContext.setPayload((prevPayload: TPayload) => ({
        ...prevPayload,
        score_weightage: scoreWeightage
      }));
    }
  }, [scoreWeightage]);

  const handleMetricChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = event.target;
    if (!checked && scoreWeightage.find(sw => sw.metric_name === value)?.is_fixed) {
      toggleLock(value);
    }
    setScoreWeightage(prevWeightage => {
      const newWeightage = checked
        ? [...prevWeightage, { metric_name: value, value: 0, is_fixed: false }]
        : prevWeightage.filter(sw => sw.metric_name !== value);
      return updatePercentages(newWeightage);
    });
  };

  const toggleLock = (metric: string) => {
    setScoreWeightage(prevWeightage => {
      const newWeightage = prevWeightage.map(sw =>
        sw.metric_name === metric ? { ...sw, is_fixed: !sw.is_fixed } : sw
      );
      return updatePercentages(newWeightage);
    });
  };

  const updatePercentages = (weightage: TScoreWeightage[]): TScoreWeightage[] => {
    const lockedMetricsCount = weightage.filter(sw => sw.is_fixed).length;
    const totalLockedPercentage = weightage.reduce((sum, sw) => sw.is_fixed ? sum + sw.value : sum, 0);
    const remainingPercentage = 100 - totalLockedPercentage;
    const unlockedMetricsCount = weightage.length - lockedMetricsCount;
    const unlockedPercentage = unlockedMetricsCount > 0 ? remainingPercentage / unlockedMetricsCount : 0;

    return weightage.map(sw => ({
      ...sw,
      value: sw.is_fixed ? sw.value : unlockedPercentage
    }));
  };

  const isCheckboxDisabled = (metric: string): boolean => {
    const totalLockedPercentage = scoreWeightage
      .filter(sw => sw.is_fixed)
      .reduce((sum, sw) => sum + sw.value, 0);
    const isMetricSelected = scoreWeightage.some(sw => sw.metric_name === metric);

    return (
      scoreWeightage.some(sw => sw.is_fixed) &&
      !isMetricSelected &&
      totalLockedPercentage >= 100
    );
  };

  return (
    <div className="tw-mb-3 tw-bg-gray-100 tw-rounded tw-border tw-border-zinc-300 tw-p-4 tw-w-[95%]">
      <label className="text-caption-style tw-text-[#747593]">{SCORE_WEIGHTAGE}</label>
      <div className="tw-grid tw-grid-cols-4 tw-gap-4 tw-mt-2">
        {METRIC_OPTIONS.map((metric) => {
          const metricId = `metric-${metric.toLowerCase()}`;
          const metricWeightage = scoreWeightage.find(sw => sw.metric_name === metric);
          const isSelected = !!metricWeightage;
          const isLocked = metricWeightage?.is_fixed || false;

          return (
            <div key={metric} className="tw-flex tw-flex-col tw-gap-2">
              <div className="tw-inline-flex tw-items-center tw-cursor-pointer tw-gap-2">
                <input 
                  type="checkbox" 
                  id={metricId} 
                  value={metric} 
                  checked={isSelected} 
                  disabled={isCheckboxDisabled(metric)} 
                  onChange={handleMetricChange} 
                  className="tw-form-checkbox tw-h-4 tw-w-4 tw-text-[#091CAA]" 
                />
                <label htmlFor={metricId} className="tw-text-sm tw-text-[#172B4D]">{metric}</label>
              </div>
              <div className='tw-flex tw-items-center tw-gap-2 tw-ml-6'>
                <input 
                  type="number" 
                  className="shadow-none drop-down-dm tw-w-[50%] tw-ps-2" 
                  value={metricWeightage?.value.toFixed(2) || ''} 
                  readOnly 
                  placeholder="%" 
                />
                <LockSwitch 
                  metric={metric} 
                  isLocked={isLocked} 
                  disabled={!isSelected || isCheckboxDisabled(metric)} 
                  toggleLock={toggleLock} 
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default ScoreWeightage;